import { useEffect, useState } from "react";
import { CornerDownRight, Link2 } from "lucide-react";
import { cornerLines, ANGLE_PRESETS } from "@/lib/corners";
import { isCorner } from "@/lib/geometry";

const seg = (on) => `px-2 text-[10px] transition-colors ${on ? "bg-pink-500 text-slate-950" : "text-slate-400 hover:bg-slate-800"} disabled:opacity-30 disabled:pointer-events-none`;

const AngleField = ({ value, onChange, testid }) => {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const commit = (x) => { const n = Math.min(180, Math.max(30, Math.round(+x) || 180)); setV(n); onChange(n); };
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <div className="relative">
        <input data-testid={testid} type="number" min={30} max={180} value={v} onChange={(e) => setV(e.target.value)} onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && commit(e.currentTarget.value)}
          className="h-7 w-20 pl-2 pr-5 rounded-md bg-[#0B0F19] border border-slate-700 font-mono text-xs text-slate-100 focus:outline-none focus:border-pink-400" />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-500">°</span>
      </div>
      {ANGLE_PRESETS.map((a) => (
        <button key={a} data-testid={`${testid}-preset-${a}`} onClick={() => commit(a)}
          className={`h-7 px-2 rounded-md border text-[10px] font-mono transition-colors ${+value === a ? "border-pink-400 text-pink-200 bg-pink-500/10" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
          {a === 180 ? "180 flat" : `${a}°`}
        </button>
      ))}
    </div>
  );
};

const Toggle = ({ options, value, onChange, testid }) => (
  <div className="flex h-7 rounded-md border border-slate-700 overflow-hidden w-fit">
    {options.map(([v, l, dis, title]) => (
      <button key={v} data-testid={`${testid}-${v}`} title={title} disabled={dis} onClick={() => onChange(v)} className={seg(value === v)}>{l}</button>
    ))}
  </div>
);

const DirToggle = ({ value, onChange, testid }) => (
  <Toggle testid={testid} value={value || "ext"} onChange={onChange} options={[["ext", "External corner"], ["int", "Internal corner"]]} />
);

const LineRow = ({ l, set }) => {
  const k = l.k || {}, angle = +k.angle || 180, on = isCorner(k);
  return (
    <div className="rounded border border-slate-700 p-2 space-y-2" data-testid={`corner-line-${l.i}`}>
      <p className="text-xs text-slate-200 flex items-center gap-2">
        <span className="font-mono font-bold text-pink-300">K{l.i}</span>Between col {l.i} and {l.i + 1} · x = {l.x} mm
        {on && <span className="ml-auto font-mono text-[10px] text-pink-200" data-testid={`corner-line-${l.i}-summary`}>{angle}° {k.type === "glass" && l.glassOk ? "glass" : "post"}</span>}
      </p>
      {!l.eligible ? (
        <p className="text-[10px] text-slate-500" data-testid={`corner-line-${l.i}-blocked`}>A panel spans across this line. Split it in Panels to fold here.</p>
      ) : (
        <>
          <AngleField testid={`corner-angle-${l.i}`} value={angle} onChange={(a) => set(l.i, { angle: a })} />
          {on && (
            <div className="flex flex-wrap gap-2">
              <Toggle testid={`corner-type-${l.i}`} value={k.type === "glass" && l.glassOk ? "glass" : "post"} onChange={(type) => set(l.i, { type })}
                options={[["post", "Corner post"], ["glass", "Glass-to-glass", !l.glassOk, l.glassOk ? "" : "Both sides must be fixed glass"]]} />
              <DirToggle testid={`corner-dir-${l.i}`} value={k.dir} onChange={(dir) => set(l.i, { dir })} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

const EdgeRow = ({ side, item, items, update }) => {
  const own = item.couplings?.[side] || {}, other = side === "left" ? "right" : "left";
  const from = items.find((o) => o.id !== item.id && o.couplings?.[other]?.tag === item.tag && o.couplings[other].tag);
  const set = (patch) => {
    const next = { angle: 90, dir: "ext", ...own, ...patch }, couplings = { ...(item.couplings || {}) };
    if (next.tag) couplings[side] = next; else delete couplings[side];
    update({ ...item, couplings });
  };
  return (
    <div className="rounded border border-slate-700 p-2 space-y-2" data-testid={`coupling-${side}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-200 w-20 capitalize">{side} edge</span>
        <select data-testid={`coupling-${side}-tag`} value={own.tag || ""} onChange={(e) => set({ tag: e.target.value })}
          className="h-7 flex-1 rounded-md bg-[#0B0F19] border border-slate-700 px-2 text-xs text-slate-100 focus:outline-none focus:border-pink-400">
          <option value="">Not coupled</option>
          {items.filter((o) => o.id !== item.id && o.tag).map((o) => <option key={o.id} value={o.tag}>{`${o.tag} · ${o.width} × ${o.height}`}</option>)}
        </select>
      </div>
      {from && !own.tag && <p className="text-[10px] text-slate-400" data-testid={`coupling-${side}-mirrored`}>Coupled from {from.tag} ({from.couplings[other].angle || 180}°). Edit it on {from.tag}.</p>}
      {own.tag && (
        <>
          <AngleField testid={`coupling-${side}-angle`} value={+own.angle || 90} onChange={(angle) => set({ angle })} />
          {isCorner(own) && <DirToggle testid={`coupling-${side}-dir`} value={own.dir} onChange={(dir) => set({ dir })} />}
        </>
      )}
    </div>
  );
};

export const CornersBox = ({ item, update, items = [] }) => {
  const lines = cornerLines(item);
  const set = (i, patch) => {
    const next = { angle: 180, type: "post", dir: "ext", ...(item.corners?.[i] || {}), ...patch }, corners = { ...(item.corners || {}) };
    if (isCorner(next)) corners[i] = next; else delete corners[i];
    update({ ...item, corners });
  };
  return (
    <div className="rounded-md border border-pink-500/30 bg-pink-500/5 p-3 space-y-2.5" data-testid="corners-box">
      <p className="font-mono text-[10px] uppercase tracking-wider text-pink-200 flex items-center gap-1.5"><CornerDownRight size={12} />Corner angles · developed elevation</p>
      <p className="text-[10px] text-slate-400">Angle between the two panels: 90° = L-corner, 135° = bay, 180° = flat.</p>
      {lines.length ? lines.map((l) => <LineRow key={l.i} l={l} set={set} />) : <p className="text-[10px] text-slate-500">Add a vertical division to fold this window at a corner.</p>}
      {items.length > 1 && (
        <>
          <p className="pt-1 font-mono text-[10px] uppercase tracking-wider text-pink-200 flex items-center gap-1.5"><Link2 size={12} />Corner to another window</p>
          {["left", "right"].map((side) => <EdgeRow key={side} side={side} item={item} items={items} update={update} />)}
        </>
      )}
    </div>
  );
};
